import { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import DragZone from "./DragZone";
import FileUploadButton from "./FileUploadButton";
import UploadIcon from "../../icon/UploadSimple.svg";
import "../../style/typography.scss";

/**
 * File upload component that supports drag and drop.
 * @param {object} props
 * @param {{ [fileType: string]: string }} props.allowedFileTypes List of accepted file types. Each key should
 * be the name of the extension and the value should be the MIME type for the file. The key is used to display
 * the allowed file types message, and the MIME type is used to check if the file type is valid.
 * @param {number} props.maxFileSizeMib The maximum size of each file in MiB.
 * @param {(file: File) => void} props.onUpload Called when a file is uploaded.
 * @param {(errors: string[]) => void} props.onError Called when errors occur with an uploaded file.
 */
function FileUpload({ allowedFileTypes, maxFileSizeMib, onUpload, onError }) {
    const [fileCriteriaMessage, setFileCriteriaMessage] = useState("");

    useEffect(() => {
        if (!allowedFileTypes) {
            throw Error(
                "'allowedFileTypes' prop cannot be undefined in the FileUpload component because the component " +
                    "won't allow uploading files without a list of allowed file types.",
            );
        }

        const fileTypeKeys = Object.keys(allowedFileTypes);

        // Create the file criteria message (indicates the accepted file types and maximum file size)
        if (fileTypeKeys.length === 0) {
            setFileCriteriaMessage(`File size no more than ${maxFileSizeMib}MB`);
        } else if (fileTypeKeys.length === 1) {
            setFileCriteriaMessage(`${fileTypeKeys[0]} only, file size no more than ${maxFileSizeMib}MB`);
        } else {
            const lastItem = fileTypeKeys.pop();
            setFileCriteriaMessage(
                `${fileTypeKeys.join(", ")} or ${lastItem}, file size no more than ${maxFileSizeMib}MB`,
            );
        }
    }, [allowedFileTypes, maxFileSizeMib]);

    /**
     * Calls the `onUpload` function when a file is uploaded.
     * @param {File} file The file that was uploaded.
     */
    function fileUploadHandler(file) {
        let errors = [];

        // Need to convert MiB to bytes because the file size is in bytes
        if (file.size > maxFileSizeMib * 1048576) {
            errors.push("File is too large");
        }

        if (
            // Check if file MIME type is allowed
            !Object.values(allowedFileTypes).includes(file.type)
            // Check if file extension is allowed (useful for unrecognized MIME types)
            && !Object.keys(allowedFileTypes).some((ext) => file.name.toLowerCase().endsWith(`.${ext.toLowerCase()}`))
        ) {
            errors.push("File type is not allowed");
        }

        if (errors.length > 0) {
            onError(errors);
            return;
        }

        onUpload(file);
    }

    return (
        <DragZone onDrop={fileUploadHandler}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "16px",
                }}
            >
                <ReactSVG src={UploadIcon} alt="upload-icon" />

                <div>
                    <div className="paragraph-5-semibold" style={{ color: "var(--colors-primary-slate-600)" }}>
                        Select a file or drag and drop here
                    </div>
                    <div className="paragraph-6-regular" style={{ color: "var(--colors-primary-slate-500)" }}>
                        {fileCriteriaMessage}
                    </div>
                </div>

                <FileUploadButton onUpload={fileUploadHandler}>Select File</FileUploadButton>
            </div>
        </DragZone>
    );
}

export default FileUpload;
